import { Module } from 'vuex';
import { Actor, IAdminActorState, IRootState } from '@types';

import { ActorModel } from '@model';

import Env from '@lib/Env';

import { ActorsApi } from '@/api';

import { defaultCharacterFilters } from '@/store/Admin/CharacterStore';
import { ActorApi } from '@/api/Admin/ActorApi';

export const defaultActorsFilters = <Actor.Filters>{
    ...defaultCharacterFilters,
    search: null,
    page: 1,
    limit: 20,
    sort: ['poradie.desc', 'meno_priezvisko.asc'],
};

const store: Module<IAdminActorState, IRootState> = {
    state: {
        actor: <ActorModel>null,
        actors: <ActorModel[]>[],
        actorsFilters: <Actor.Filters>{ ...defaultActorsFilters },
        actorsMeta: null,
        actorsLoading: true,
        allActors: [],
        actorProfile: null,
    },

    mutations: {
        setActor: (state, actor: ActorModel) => (state.actor = actor),
        setActorsFilters: (state, filters) => (state.actorsFilters = { ...defaultActorsFilters, ...filters }),
        appendActorsFilters: (state, filters) =>
            (state.actorsFilters = { ...defaultActorsFilters, ...state.actorsFilters, ...filters }),
        setCharacterToActor(state, { voiceId, characterId }) {
            let actor = state.actors.find((actor: ActorModel) => actor.id === voiceId);
            if (actor) actor.character_id = characterId;
        },
        setAllActors: (state, allActors) => (state.allActors = allActors),
        setActorProfile: (state, actorProfile) => (state.actorProfile = actorProfile),
    },

    getters: {
        getAdminActors: (state): ActorModel[] => state.actors,
        getActorsLoading: (state): boolean => state.actorsLoading,
        getActorsFilters: (state): any => ({ ...defaultActorsFilters, ...state.actorsFilters }),
        getActorsMeta: (state): boolean => state.actorsMeta,
        getAllActors: (state): any[] => state.allActors,
        getActorProfile: (state): any => state.actorProfile,
    },

    actions: {
        setCharacterToActors({ commit }, { voices, characterId }) {
            voices.forEach((voice: any) => {
                commit('setCharacterToActor', { voiceId: voice.id, characterId: characterId });
            });
        },

        async loadListActors(params: any): Promise<void> {
            const { data } = await ActorsApi.loadList(params);

            return data;
        },

        async loadActorProfile({ commit }, params: any): Promise<void> {
            const { data } = await ActorApi.loadActor(params);

            commit('setActorProfile', data);
        },

        async saveActorProfile({ commit }, params: any): Promise<any> {
            const { data } = await ActorApi.saveActor(params);

            return data;
        },

        async updateVoice({ commit }, formData: FormData): Promise<any> {
            const { data } = await ActorApi.updateVoice(formData);

            return data;
        },

        async loadAllActors({ commit }) {
            const { data } = await ActorsApi.loadAll();

            commit('setAllActors', data);
        },

        async loadActors({ getters, state }, params: any): Promise<ActorModel[]> {
            state.actorsLoading = true;
            try {
                // !!! Do not add include to query
                const query = { ...getters.getActorsFilters, ...params, visible: 1 };
                const { data } = await ActorsApi.load(null, query);
                const blacklist = getters.getProject.blacklist_ids;
                state.actors = data.records.map((item: any, index: number) => {
                    const actor = new ActorModel(item);

                    // TODO: maybe relocate somewhere else
                    // Set list indexes
                    actor.index = index + 1 + (data.meta.pagination.current_page - 1) * data.meta.pagination.per_page;

                    // Set characted ids
                    actor.character_id = getters.getProject?.characters?.reduce((result: number, char: any) => {
                        if (result) {
                            return result;
                        }
                        if (char.candidates.map((item: any) => item.id).includes(actor.id)) {
                            result = char.id;
                        }
                        return result;
                    }, null);

                    // TODO - prepísať v službe poslať character_id=-1 - potom to vôbec netreba
                    if (blacklist && blacklist.includes(actor.id)) {
                        actor.character_id = -1;
                    }
                    return actor;
                });
                state.actorsMeta = data.meta;
                return state.actors;
            } catch (e) {
                Env.log.error(e);
                throw e;
            } finally {
                state.actorsLoading = false;
            }
        },

        async loadActorsEmails({ getters, state }, params: any): Promise<ActorModel[]> {
            state.actorsLoading = true;
            try {
                const query = { ...getters.getActorsFilters, ...params, visible: 1, include: [] };
                const { data } = await ActorsApi.loadEmails(query);
                return data.records;
            } catch (e) {
                Env.log.error(e);
                throw e;
            } finally {
                state.actorsLoading = false;
            }
        },

        async storePrice({ commit }, data) {
            await ActorApi.savePrice(data);
        },
    },
};

export default store;
